import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const ROLE_CHECK_TIMEOUT = 10000;

export const useSchoolAdminCheck = () => {
  const { user } = useAuth();
  const [isSchoolAdmin, setIsSchoolAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastCheckedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkRole = async () => {
      if (!user || user.id !== lastCheckedUserIdRef.current) {
        setIsSchoolAdmin(false);
        setLoading(true);
        lastCheckedUserIdRef.current = user?.id || null;
      }

      if (!user) {
        setLoading(false);
        return;
      }

      timeoutId = setTimeout(() => {
        if (!cancelled) {
          console.warn('[useSchoolAdminCheck] Safety timeout reached, forcing loading=false');
          setLoading(false);
        }
      }, ROLE_CHECK_TIMEOUT);

      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'school_admin'
        });

        if (cancelled) return;

        if (error) {
          console.error('[useSchoolAdminCheck] Error:', error);
          setIsSchoolAdmin(false);
        } else {
          setIsSchoolAdmin(data === true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[useSchoolAdminCheck] Exception:', error);
        setIsSchoolAdmin(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    checkRole();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user?.id]);

  return { isSchoolAdmin, loading };
};
